import { formatDateSafe } from '@/utils/dateUtils';
import type { VacationRequest } from '../models/vacation.types';
import type { VacationEvent, VacationEventType } from '../models/vacationEvent.types';
import type { VacationTimelineTone } from './vacationTimeline';

const EVENT_LABELS: Record<VacationEventType, string> = {
    request_created: 'Solicitação enviada',
    request_opened: 'Solicitação enviada ao gestor',
    manager_approved: 'Aprovada pelo gestor',
    manager_rejected: 'Reprovada pelo gestor',
    client_approved: 'Aprovada pelo cliente',
    dp_approved: 'Validada pelo DP',
    dp_rejected: 'Reprovada pelo DP',
    dp_segment_approved: 'Período validado pelo DP',
    dp_segment_rejected: 'Período reprovado pelo DP',
    client_segment_approved: 'Período aceito pelo cliente',
    client_segment_rejected: 'Período recusado pelo cliente',
    correction_submitted: 'Período corrigido pelo colaborador',
    expired_rejected: 'Expirada por prazo vencido',
    sell_approved: 'Abono pecuniário aprovado',
    sell_rejected: 'Abono pecuniário reprovado',
    vacation_started: 'Férias iniciadas',
    vacation_completed: 'Férias concluídas',
    direct_edit: 'Edição direta (admin)',
};

const CORRECTION_MANAGER_LABELS = {
    manager_approved: 'Correção aprovada pelo gestor',
    manager_rejected: 'Correção devolvida pelo gestor',
} as const;

const EVENT_TONES: Partial<Record<VacationEventType, VacationTimelineTone>> = {
    manager_rejected: 'rejected',
    dp_rejected: 'rejected',
    dp_segment_rejected: 'rejected',
    client_segment_rejected: 'rejected',
    sell_rejected: 'rejected',
    expired_rejected: 'rejected',
    direct_edit: 'muted',
};

const TIMELINE_KEYS: Partial<Record<VacationEventType, string>> = {
    request_created: 'requested',
    request_opened: 'requested',
    manager_approved: 'manager',
    manager_rejected: 'manager',
    client_approved: 'client',
    dp_approved: 'dp',
    dp_rejected: 'dp',
    sell_approved: 'sell',
    sell_rejected: 'sell',
};

export const FRUITION_EVENT_TYPES: readonly VacationEventType[] = ['vacation_started', 'vacation_completed'];

export function resolveEventTimelineKey(type: VacationEventType): string {
    return TIMELINE_KEYS[type] ?? `event_${type}`;
}

export function resolveEventTone(type: VacationEventType): VacationTimelineTone {
    return EVENT_TONES[type] ?? 'done';
}

export function readRevisionFromId(id: string): number | null {
    const match = /_r(\d+)_/.exec(id);
    return match ? Number(match[1]) : null;
}

function readRevision(event: VacationEvent): number {
    const fromPayload = event.payload?.revision;
    if (typeof fromPayload === 'number') return fromPayload;
    return readRevisionFromId(event.id) ?? 0;
}

function readSegmentOrder(event: VacationEvent): number | null {
    const fromPayload = event.payload?.segmentOrder;
    if (typeof fromPayload === 'number') return fromPayload;
    const match = /^(?:dp|client)_segment_(?:approved|rejected)_(\d+)_/.exec(event.id);
    return match ? Number(match[1]) : null;
}

function readDecisionMs(event: VacationEvent): number | null {
    const match = /_(\d{10,})$/.exec(event.id);
    return match ? Number(match[1]) : null;
}

function formatRange(startDate?: unknown, endDate?: unknown): string | null {
    if (typeof startDate !== 'string' || typeof endDate !== 'string') return null;
    return `${formatDateSafe(startDate)} a ${formatDateSafe(endDate)}`;
}

function resolveSegmentRange(event: VacationEvent, request: VacationRequest): string | null {
    const fromPayload = formatRange(event.payload?.startDate, event.payload?.endDate);
    if (fromPayload) return fromPayload;

    const order = readSegmentOrder(event);
    if (order === null) return null;

    const decisions = event.type.startsWith('client_') ? request.clientSegmentDecisions : request.dpSegmentDecisions;
    const decision = decisions?.[String(order)];
    const segment = request.segments.find((item) => item.order === order);

    if (event.type.endsWith('_segment_approved') && decision?.status === 'Approved' && segment) {
        return formatRange(segment.startDate, segment.endDate);
    }

    const sameDecision =
        decision?.status === 'Rejected' && decision.at?.toMillis?.() === readDecisionMs(event);
    return sameDecision ? formatRange(decision?.startDate, decision?.endDate) : null;
}

export function buildRecordedEventLabel(event: VacationEvent, request: VacationRequest): string {
    if (event.type.endsWith('_segment_approved') || event.type.endsWith('_segment_rejected')) {
        const range = resolveSegmentRange(event, request);
        const approved = event.type.endsWith('_segment_approved');
        const by = event.type.startsWith('client_') ? 'cliente' : 'DP';
        const verb = event.type.startsWith('client_')
            ? (approved ? 'aceito' : 'recusado')
            : (approved ? 'validado' : 'reprovado');
        return range ? `Período de ${range} ${verb} pelo ${by}` : EVENT_LABELS[event.type];
    }

    if (
        (event.type === 'manager_approved' || event.type === 'manager_rejected') &&
        readRevision(event) > 0
    ) {
        return CORRECTION_MANAGER_LABELS[event.type];
    }

    return EVENT_LABELS[event.type] ?? event.type;
}

export function buildCorrectionNote(event: VacationEvent): string | null {
    const before = formatRange(event.payload?.previousStartDate, event.payload?.previousEndDate);
    const after = formatRange(event.payload?.startDate, event.payload?.endDate);
    if (before && after) return `${before} → ${after}`;
    return after ? `Nova data: ${after}` : null;
}
